import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useStoreActions } from "easy-peasy";
import { requestGoogleAccessToken, loadGoogleIdentityServices } from "../lib/google-identity";
import { authenticateWithGoogle } from "../api/google-auth";

export const SignInWithGoogle = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const history = useHistory();
  const setIsAuthenticated = useStoreActions(
    (actions) => actions.setIsAuthenticated
  );

  useEffect(() => {
    let isMounted = true;

    loadGoogleIdentityServices()
      .then(() => {
        if (isMounted) {
          setIsReady(true);
        }
      })
      .catch((error) => {
        console.error("Failed to load Google Identity Services:", error);
        if (isMounted) {
          setIsReady(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSignIn = async () => {
    try {
      setIsLoading(true);

      const tokenResponse = await requestGoogleAccessToken();

      await authenticateWithGoogle({
        accessToken: tokenResponse.access_token,
        idToken: tokenResponse.id_token,
      });

      setIsAuthenticated(true);
      history.push("/applications");
    } catch (error) {
      console.error("Google sign-in error:", error);

      if (error.response?.data?.error === "account_not_connected") {
        history.push("/account-not-connected");
      } else if (error.message === "popup_closed" || error.code === "popup_closed") {
        // User dismissed the popup; no alert needed.
      } else {
        alert(
          error.response?.data?.message ||
            error.message ||
            "Failed to sign in with Google. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isDisabled = isLoading || !isReady;

  return (
    <button
      onClick={handleSignIn}
      disabled={isDisabled}
      className="mb-2 w-full py-3 flex items-center justify-center bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded cursor-pointer transition-all duration-200"
    >
      {isLoading ? (
        <>
          <svg
            className="animate-spin h-5 w-5 mr-3"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          Signing in...
        </>
      ) : (
        <>
          <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Sign in with Google
        </>
      )}
    </button>
  );
};
